import { API_BASE_URL } from '@/lib/constants'
import { getToken } from '@/stores/auth'
import { apiRequest } from './client'
import type { RequestLog } from './logs'
import type { RequestDocs } from './endpoints'

// 認証ヘッダーを付けない素の fetch ラッパー。ログイン中ならトークンがある場合のみ付与する
// (ギャラリー一覧は未ログインでも見られるが、ログイン中は「いいね済みか」も返したいため)。
// 本番では API_BASE_URL (https://app.scenariomock.com) を prefix として使う。
// 開発では Vite .env の VITE_API_BASE_URL (http://app.lvh.me:3000) が入る。
async function publicRequest<T>(
  path: string,
  options?: { method?: 'GET' | 'POST' | 'DELETE'; body?: unknown },
): Promise<T> {
  const token = getToken()
  const hasBody = options?.body !== undefined
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: options?.method ?? 'GET',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: hasBody ? JSON.stringify(options?.body) : undefined,
  })
  if (!res.ok) {
    let body: unknown
    try { body = await res.json() } catch { body = null }
    const b = body as Record<string, unknown>
    const message =
      typeof b?.error === 'object' && b.error !== null && 'message' in (b.error as object)
        ? String((b.error as Record<string, unknown>).message)
        : res.statusText
    throw new Error(message)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export interface PublicProject {
  id: string
  slug: string
  name: string
  description: string | null
  visibility: string
  owner_user_id: string
  is_shared: boolean
}

export interface PublicEndpoint {
  id: string
  project_id: string
  method: string
  path: string
  description: string | null
  priority: number
  request_docs: RequestDocs
}

export interface PublicScenario {
  id: string
  name: string
  type: string
  priority: number
  enabled: boolean
  config: unknown
}

export function getPublicProject(slug: string): Promise<PublicProject> {
  return publicRequest<PublicProject>(`/api/public/projects/${slug}`)
}

export function getPublicEndpoints(slug: string): Promise<PublicEndpoint[]> {
  return publicRequest<PublicEndpoint[]>(`/api/public/projects/${slug}/endpoints`)
}

export function getPublicEndpointScenarios(slug: string, endpointId: string): Promise<PublicScenario[]> {
  return publicRequest<PublicScenario[]>(`/api/public/projects/${slug}/endpoints/${endpointId}/scenarios`)
}

export function getPublicScenarioState(scenarioId: string): Promise<{ counts: Record<string, number> }> {
  return publicRequest<{ counts: Record<string, number> }>(`/api/public/scenarios/${scenarioId}/state`)
}

export async function getPublicLogs(
  slug: string,
  opts?: { limit?: number; before?: string },
): Promise<RequestLog[]> {
  const params = new URLSearchParams()
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit))
  if (opts?.before) params.set('before', opts.before)
  const qs = params.toString()
  return publicRequest<RequestLog[]>(`/api/public/projects/${slug}/logs${qs ? `?${qs}` : ''}`)
}

// ── 共有ギャラリー ────────────────────────────────────────────────────────

export interface GalleryItem {
  slug: string
  name: string
  description: string | null
  ownerDisplayName: string | null
  likeCount: number
  sharedAt: string | null
  liked: boolean
}

export interface GalleryPage {
  items: GalleryItem[]
  hasMore: boolean
}

export function getGallery(opts?: {
  sort?: 'new' | 'popular'
  limit?: number
  offset?: number
}): Promise<GalleryPage> {
  const params = new URLSearchParams()
  if (opts?.sort) params.set('sort', opts.sort)
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit))
  if (opts?.offset !== undefined) params.set('offset', String(opts.offset))
  const qs = params.toString()
  return publicRequest<GalleryPage>(`/api/public/gallery${qs ? `?${qs}` : ''}`)
}

// 通報は要ログイン(いいねと同様。無意味な匿名通報を防ぐため)
export function reportProject(slug: string, reason?: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/api/public/projects/${slug}/report`, {
    method: 'POST',
    body: { reason },
  })
}

export interface LikeResult {
  liked: boolean
  likeCount: number
}

// いいね/取り消しは要ログイン(自作自演は自分のプロジェクトへの POST がサーバー側で 403 になる)
export function likeProject(slug: string): Promise<LikeResult> {
  return apiRequest<LikeResult>(`/api/public/projects/${slug}/like`, { method: 'POST' })
}

export function unlikeProject(slug: string): Promise<LikeResult> {
  return apiRequest<LikeResult>(`/api/public/projects/${slug}/like`, { method: 'DELETE' })
}

export interface PlanLimits {
  projects: number | null           // null は無制限
  endpointsPerProject: number | null
  requestsPerMinute: number
  logRetentionDays: number
}

export interface PlanLimitsResponse {
  free: PlanLimits
  pro: PlanLimits
}

export async function getPlanLimits(): Promise<PlanLimitsResponse> {
  return publicRequest<PlanLimitsResponse>('/api/public/plan-limits')
}
